import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Star, Loader2, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

function Stars({ n }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={15} strokeWidth={1.6} className={i <= n ? "fill-amber-400 text-amber-400" : "text-obsidian/20"} />
      ))}
    </div>
  );
}

export default function CoachReviews() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = (f = filter) => {
    const q = f === "published" ? "?published=true" : f === "unpublished" ? "?published=false" : "";
    api.get(`/admin/reviews${q}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const togglePublish = async (rev) => {
    setBusy(rev.review_id);
    try {
      await api.patch(`/admin/reviews/${rev.review_id}`, { published: !rev.published });
      toast.success(!rev.published ? "Avis marqué comme publiable" : "Avis retiré des témoignages");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(null); }
  };

  const removeReview = async (rev) => {
    if (!window.confirm(`Supprimer définitivement l'avis de ${rev.user_name} ?`)) return;
    setBusy(rev.review_id);
    try {
      await api.delete(`/admin/reviews/${rev.review_id}`);
      toast.success("Avis supprimé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(null); }
  };

  return (
    <CoachLayout>
      <div className="max-w-4xl">
        <h1 className="text-2xl font-light tracking-tight">Avis clients</h1>
        {data && (
          <div className="mt-5 grid grid-cols-3 gap-3" data-testid="reviews-stats">
            <Stat label="Note moyenne" value={data.average ? `${data.average}/5` : "—"} sub={<Stars n={Math.round(data.average || 0)} />} />
            <Stat label="Avis reçus" value={data.count} />
            <Stat label="Publiables" value={data.published_count} />
          </div>
        )}

        <div className="mt-6 flex gap-2">
          {[["all", "Tous"], ["published", "Publiables"], ["unpublished", "Non publiés"]].map(([k, l]) => (
            <button key={k} data-testid={`reviews-filter-${k}`} onClick={() => setFilter(k)}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${filter === k ? "bg-obsidian text-white" : "border border-border hover:border-obsidian/40"}`}>{l}</button>
          ))}
        </div>

        {!data ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-obsidian/40" /></div>
        ) : data.reviews.length === 0 ? (
          <p className="mt-8 text-sm text-obsidian/50" data-testid="reviews-empty">Aucun avis pour ce filtre.</p>
        ) : (
          <div className="mt-5 space-y-3" data-testid="reviews-list">
            {data.reviews.map((r) => (
              <div key={r.review_id} data-testid={`review-${r.review_id}`} className="rounded-2xl border border-border bg-white p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Stars n={r.rating} />
                      <span className="text-sm font-medium">{r.user_name}</span>
                      {r.plan && <span className="rounded-full bg-obsidian/6 px-2 py-0.5 text-[10px] uppercase tracking-wide text-obsidian/60">{r.plan}</span>}
                      {r.is_sample && <span data-testid={`review-sample-${r.review_id}`} className="rounded-full bg-amber-400/15 text-amber-600 px-2 py-0.5 text-[10px] uppercase tracking-wide">démo</span>}
                    </div>
                    {r.comment && <p className="mt-2 text-sm text-obsidian/70 leading-relaxed">« {r.comment} »</p>}
                    <p className="mt-2 text-[11px] text-obsidian/40">{(r.updated_at || r.created_at || "").slice(0, 10)}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <button data-testid={`review-publish-${r.review_id}`} onClick={() => togglePublish(r)} disabled={busy === r.review_id}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${r.published ? "bg-sage/15 text-sage" : "border border-border hover:border-obsidian/40"}`}>
                      {busy === r.review_id ? <Loader2 size={13} className="animate-spin" /> : (r.published ? <Eye size={13} /> : <EyeOff size={13} />)}
                      {r.published ? "Publiable" : "Publier"}
                    </button>
                    <button data-testid={`review-delete-${r.review_id}`} onClick={() => removeReview(r)} disabled={busy === r.review_id}
                      className="inline-flex items-center justify-center rounded-full w-8 h-8 border border-border text-obsidian/40 hover:text-destructive hover:border-destructive/40 transition-colors disabled:opacity-50">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CoachLayout>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4">
      <div className="text-2xl font-light">{value}</div>
      <div className="text-[11px] text-obsidian/50 mt-0.5">{label}</div>
      {sub && <div className="mt-1.5">{sub}</div>}
    </div>
  );
}
